import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOrgCurrency } from '../../hooks/useOrgCurrency.js';
import { formatMoney } from '../../utils/formatMoney.js';
import { useFormat } from '../../i18n/format.js';
import { AlertTriangle } from 'lucide-react';
import {
  useVehicles, useDrivers, useClaims, useMaintenance
} from '../../index.js';
import SimpleTable from '../common/SimpleTable.jsx';
import api, { apiRequest } from '../../api.js';
import { CLOSED_CLAIM_STATUSES } from '../../hooks/useVehicleInsuranceData.js';
import SkeletonLoader from '../common/SkeletonLoader.jsx';

// ============================================================================
// FLEET COCKPIT VIEW - Simplified Design
// Matches the clean, minimal UI pattern
// ============================================================================

// Simple sidebar tabs (text only, no icons)
// Piège #1 (constante de module) : pas de `label` en dur ici, on résout au rendu via
// SIDEBAR_LABEL_KEYS + t(cockpit.sidebar.<key>).
const SIDEBAR_TABS = [
  { key: 'cockpit' },
  { key: 'flotte' },
  { key: 'alertes' },
  { key: 'couts' },
  { key: 'utilisation' },
];

const SIDEBAR_LABEL_KEYS = {
  cockpit: 'cockpit.sidebar.cockpit',
  flotte: 'cockpit.sidebar.fleet',
  alertes: 'cockpit.sidebar.alerts',
  couts: 'cockpit.sidebar.costs',
  utilisation: 'cockpit.sidebar.utilization',
};

// ============================================================================
// COST ANALYSIS TAB - Cost Efficiency KPIs
// Source unique : moteur KPI (/kpis/costs + /kpis/costs/by-vehicle, 12 mois glissants).
// Jusqu'au 2026-08-21 cet onglet additionnait lui-même maintenance (tous statuts, sans
// fenêtre) + sinistres bruts (sans indemnité) — et concaténait des NUMERIC PG rendus en
// string. Rabattu sur le moteur ; gardé par test_tco_reconciliation.mjs.
// ============================================================================
const fmtMoney = (v, currency = 'EUR') => formatMoney(v, currency, { decimals: 0 });

function CostAnalysisTab() {
  const { t } = useFormat();
  // Devise de référence Groupe (backend/shared/currency.mjs REFERENCE_CURRENCY) — jamais
  // un symbole en dur, exposée par le résolveur unique useOrgCurrency.
  const { referenceCurrency } = useOrgCurrency();
  const [state, setState] = useState({ loading: true, error: null, summary: null, vehicles: [] });

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.kpi.getCostBreakdown('year'), api.kpi.getCostsByVehicle('year')])
      .then(([summaryRes, byVehicleRes]) => {
        if (cancelled) return;
        setState({
          loading: false,
          error: null,
          summary: summaryRes?.data || null,
          vehicles: byVehicleRes?.data?.vehicles || []
        });
      })
      .catch(err => { if (!cancelled) setState({ loading: false, error: err.message || null, summary: null, vehicles: [] }); });
    return () => { cancelled = true; };
  }, []);

  const { summary, vehicles } = state;
  const currency = summary?.currency || 'EUR';
  const multiCurrency = new Set(vehicles.map(v => v.currency)).size > 1;

  // Top 10 : classement en devise de référence (seul comparable entre filiales)
  const costlyVehicles = useMemo(() => (
    [...vehicles]
      .filter(v => Number(v.total) > 0)
      .sort((a, b) => Number(b.total_ref ?? b.total) - Number(a.total_ref ?? a.total))
      .slice(0, 10)
  ), [vehicles]);

  if (state.loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-4 gap-4">
          {Array(4).fill(0).map((_, i) => <SkeletonLoader key={i} type="widget" />)}
        </div>
        <SkeletonLoader type="table" rows={10} columns={5} />
      </div>
    );
  }
  if (state.error) {
    return <div className="text-red-600 text-sm">{state.error || t('common.states.loadError')}</div>;
  }

  const breakdown = summary?.breakdown || {};
  const withCur = (v, row) => fmtMoney(v, row?.currency || currency);

  return (
    <div className="space-y-6">
      {/* KPI Cards — 12 mois glissants, moteur KPI */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="text-gray-500 text-sm">{t('cockpit.costs.kpi.maintenanceTotal')}</h4>
          <div className="text-2xl font-bold text-blue-600">{fmtMoney(breakdown.maintenance, currency)}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="text-gray-500 text-sm">{t('cockpit.costs.kpi.claimsTotal')}</h4>
          <div className="text-2xl font-bold text-red-600">{fmtMoney(breakdown.claims, currency)}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="text-gray-500 text-sm">{t('cockpit.costs.kpi.fleetTco')}</h4>
          <div className="text-2xl font-bold text-amber-600">{fmtMoney(summary?.grandTotal, currency)}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="text-gray-500 text-sm">{t('costs.overview.avgPerVehicle')}</h4>
          <div className="text-2xl font-bold text-gray-700">{fmtMoney(summary?.avgPerVehicle, currency)}</div>
        </div>
      </div>
      <p className="text-xs text-gray-500 -mt-3">{t('cockpit.costs.rollingNote')}</p>

      {/* Cost by Vehicle Table */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium mb-4">{t('cockpit.costs.topVehiclesTitle')}</h3>
        <SimpleTable
          columns={[
            { key: 'plate', label: t('costs.export.plate') },
            { key: 'model', label: t('vehicles.list.columns.vehicle'), render: (_, row) => `${row.brand || ''} ${row.model || ''}`.trim() },
            ...(multiCurrency ? [{ key: 'org_name', label: t('costs.byOrg.colSubsidiary') }] : []),
            { key: 'maintenance', label: t('common.costFamilies.maintenance'), render: (_, row) => withCur(row.breakdown?.maintenance, row) },
            { key: 'claims', label: t('common.costFamilies.claims'), render: (_, row) => withCur(row.breakdown?.claims, row) },
            { key: 'total', label: t('cockpit.costs.colTco'), render: (v, row) => <span className="font-bold">{withCur(v, row)}</span> },
            // total_ref est déjà converti par le moteur KPI dans la devise de référence
            // Groupe (backend REFERENCE_CURRENCY) — on l'affiche dans cette même devise,
            // résolue ici via useOrgCurrency, jamais un symbole recopié.
            ...(multiCurrency ? [{ key: 'total_ref', label: t('cockpit.costs.kpi.tcoRef', { currency: referenceCurrency }), render: (v) => <span className="font-bold">{fmtMoney(v, referenceCurrency)}</span> }] : []),
          ]}
          data={costlyVehicles}
          pageSize={10}
          emptyMessage={t('common.tabs.coutsTab.noCostRecordedTitle')}
          compact
        />
      </div>
    </div>
  );
}

// ============================================================================
// UTILIZATION TAB - Fleet Utilization & Multi-Org KPIs
// ============================================================================
function UtilizationTab({ vehicles, drivers }) {
  const { t, fmt } = useFormat();
  // Piège #2 (useMemo fige) : la valeur traduite 'Inconnu' devient une clé de regroupement
  // — t doit être en dépendance pour que le groupe se reconstruise au changement de langue.
  const utilizationMetrics = useMemo(() => {
    // Vehicle status distribution
    const statusCounts = {};
    vehicles.forEach(v => {
      statusCounts[v.status] = (statusCounts[v.status] || 0) + 1;
    });

    // Calculate by organization
    const unknownOrg = t('cockpit.utilization.unknownOrg');
    const orgMetrics = {};
    vehicles.forEach(v => {
      const orgId = v.org_id || unknownOrg;
      orgMetrics[orgId] = orgMetrics[orgId] || { vehicles: 0, active: 0, mileage: 0 };
      orgMetrics[orgId].vehicles += 1;
      if (v.status === 'active') orgMetrics[orgId].active += 1;
      orgMetrics[orgId].mileage += (v.mileage || 0);
    });

    // Calculate by vehicle type/category
    const unknownType = t('cockpit.utilization.unknownType');
    const typeCounts = {};
    vehicles.forEach(v => {
      const type = v.vehicle_type || v.category || unknownType;
      typeCounts[type] = (typeCounts[type] || 0) + 1;
    });

    // Driver utilization
    const assignedDrivers = drivers.filter(d => d.status === 'active').length;
    const driverUtilization = drivers.length > 0 ? (assignedDrivers / drivers.length) * 100 : 0;

    // Average mileage
    const totalMileage = vehicles.reduce((sum, v) => sum + (v.mileage || 0), 0);
    const avgMileage = vehicles.length > 0 ? totalMileage / vehicles.length : 0;

    return {
      statusCounts,
      orgMetrics,
      typeCounts,
      assignedDrivers,
      totalDrivers: drivers.length,
      driverUtilization,
      avgMileage,
      totalVehicles: vehicles.length
    };
  }, [vehicles, drivers, t]);

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="text-gray-500 text-sm">{t('cockpit.utilization.kpi.activeVehicles')}</h4>
          <div className="text-2xl font-bold text-green-600">
            {utilizationMetrics.statusCounts.active || 0} / {utilizationMetrics.totalVehicles}
          </div>
          <div className="text-xs text-gray-400">
            {t('cockpit.utilization.availability', { percent: ((utilizationMetrics.statusCounts.active || 0) / Math.max(utilizationMetrics.totalVehicles, 1) * 100).toFixed(0) })}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="text-gray-500 text-sm">{t('cockpit.utilization.kpi.activeDrivers')}</h4>
          <div className="text-2xl font-bold text-blue-600">
            {utilizationMetrics.assignedDrivers} / {utilizationMetrics.totalDrivers}
          </div>
          <div className="text-xs text-gray-400">
            {t('cockpit.utilization.usage', { percent: utilizationMetrics.driverUtilization.toFixed(0) })}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="text-gray-500 text-sm">{t('cockpit.utilization.kpi.avgMileage')}</h4>
          <div className="text-2xl font-bold text-amber-600">
            {fmt.number(utilizationMetrics.avgMileage, { decimals: 0 })} km
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="text-gray-500 text-sm">{t('cockpit.utilization.kpi.organizations')}</h4>
          <div className="text-2xl font-bold text-purple-600">
            {Object.keys(utilizationMetrics.orgMetrics).length}
          </div>
        </div>
      </div>

      {/* Status Distribution */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium mb-4">{t('cockpit.utilization.byStatus')}</h3>
          <div className="space-y-3">
            {Object.entries(utilizationMetrics.statusCounts).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <span className="capitalize">{status}</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${
                        status === 'active' ? 'bg-green-500' :
                        status === 'maintenance' ? 'bg-orange-500' :
                        status === 'retired' ? 'bg-gray-500' :
                        'bg-blue-500'
                      }`}
                      style={{ width: `${(count / utilizationMetrics.totalVehicles) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium w-8 text-right">{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium mb-4">{t('cockpit.utilization.byType')}</h3>
          <div className="space-y-3">
            {Object.entries(utilizationMetrics.typeCounts).slice(0, 6).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between">
                <span className="capitalize">{type}</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-500"
                      style={{ width: `${(count / utilizationMetrics.totalVehicles) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium w-8 text-right">{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Per-Organization Metrics */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium mb-4">{t('cockpit.utilization.byOrg')}</h3>
        <SimpleTable
          columns={[
            { key: 'org', label: t('cockpit.utilization.colOrganization') },
            { key: 'vehicles', label: t('costs.common.vehiclesLabel') },
            { key: 'active', label: t('dashboard.fleet.active') },
            { key: 'availability', label: t('cockpit.utilization.colAvailability'), render: (_, row) => `${((row.active / row.vehicles) * 100).toFixed(0)}%` },
            { key: 'mileage', label: t('cockpit.utilization.colMileageTotal'), render: (v) => fmt.number(v) },
          ]}
          data={Object.entries(utilizationMetrics.orgMetrics).map(([org, data]) => ({
            org,
            ...data,
            id: org
          }))}
          pageSize={10}
          emptyMessage={t('cockpit.utilization.emptyOrg')}
          compact
        />
      </div>
    </div>
  );
}

// ============================================================================
// GDPR COMPLIANCE SUMMARY - Source unique : /gdpr-compliance/overview-stats
// (même endpoint et même clé de cache que GDPRComplianceView.jsx). Jusqu'au
// 2026-08-23 ce bloc affichait "85 %" / "3 / 4 consentements" codés en dur.
// Le score et le compte de consentements viennent du backend, tenant-isolé et
// borné au périmètre actif (X-Active-Org, posé automatiquement par apiRequest).
// ============================================================================
function GdprComplianceSummary() {
  const { t } = useFormat();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['gdpr_overview_stats'],
    queryFn: () => apiRequest('/gdpr-compliance/overview-stats'),
    staleTime: 30000,
  });

  if (isLoading) {
    return <SkeletonLoader type="widget" />;
  }
  if (isError) {
    return <div className="text-red-600 text-sm">{t('common.states.loadError')}</div>;
  }

  const stats = data?.data || {};
  const drivers = stats.drivers || { total: 0, with_consent: 0 };
  const score = Number.isFinite(stats.compliance_score) ? stats.compliance_score : 0;
  const scoreColor = score >= 80 ? 'text-green-600' : score >= 50 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="flex items-center gap-8">
      <div>
        <span className="text-gray-500 text-sm">{t('cockpit.main.gdprOverall')}</span>
        <div className={`text-4xl font-bold ${scoreColor}`}>{score} %</div>
      </div>
      <div className="text-sm text-gray-500">
        {t('cockpit.main.gdprConsents', { count: drivers.with_consent, total: drivers.total })}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COCKPIT COMPONENT
// ============================================================================
function FleetCockpitView({ onNavigate }) {
  // Cout consolide du perimetre : devise de reference, jamais EUR en dur.
  const { moneyScope, scopeSymbol } = useOrgCurrency();
  const { t, fmt } = useFormat();
  const [activeTab, setActiveTab] = useState('cockpit');

  // Data hooks - only load what we need
  const { data: vehicles = [], isLoading: vehiclesLoading } = useVehicles();
  const { data: drivers = [], isLoading: driversLoading } = useDrivers();
  const { data: claims = [], isLoading: claimsLoading } = useClaims();
  const { data: maintenance = [], isLoading: maintenanceLoading } = useMaintenance();

  const isLoading = vehiclesLoading || driversLoading || claimsLoading || maintenanceLoading;

  // Calculate stats
  const stats = useMemo(() => {
    const activeVehicles = vehicles.filter(v => v.status === 'active').length;
    const openClaims = claims.filter(c => !CLOSED_CLAIM_STATUSES.includes(c.status)).length;
    const activeMaintenance = maintenance.filter(m => !['completed', 'cancelled'].includes(m.status)).length;
    const availabilityRate = vehicles.length > 0 ? (activeVehicles / vehicles.length) * 100 : 100;

    return { activeVehicles, totalVehicles: vehicles.length, openClaims, activeMaintenance, availabilityRate };
  }, [vehicles, claims, maintenance]);

  // Calculate alerts
  const alerts = useMemo(() => {
    const now = new Date();
    const soon = new Date();
    soon.setDate(soon.getDate() + 30);

    const expiredLicenses = drivers.filter(d => d.license_expiry && new Date(d.license_expiry) < now);
    const ctOverdue = vehicles.filter(v => v.next_service_due && new Date(v.next_service_due) < now);
    const lldEnding = vehicles.filter(v => v.financing_end_date && new Date(v.financing_end_date) < soon);
    const maintenanceDue = vehicles.filter(v => v.next_service_due && new Date(v.next_service_due) < soon && new Date(v.next_service_due) >= now);

    return { expiredLicenses, ctOverdue, lldEnding, maintenanceDue };
  }, [drivers, vehicles]);

  // Top problematic vehicles (by cost or issues)
  const problematicVehicles = useMemo(() => {
    // Simple calculation: vehicles with claims or maintenance
    const vehicleIssues = {};

    claims.forEach(c => {
      if (c.vehicle_id && !CLOSED_CLAIM_STATUSES.includes(c.status)) {
        vehicleIssues[c.vehicle_id] = (vehicleIssues[c.vehicle_id] || 0) + (c.estimated_amount || 500);
      }
    });

    maintenance.forEach(m => {
      if (m.vehicle_id && !['completed', 'cancelled'].includes(m.status)) {
        vehicleIssues[m.vehicle_id] = (vehicleIssues[m.vehicle_id] || 0) + (m.total_cost || 100);
      }
    });

    return vehicles
      .filter(v => vehicleIssues[v.id])
      .map(v => ({ ...v, cost: vehicleIssues[v.id] || 0 }))
      .sort((a, b) => b.cost - a.cost)
      .slice(0, 5);
  }, [vehicles, claims, maintenance]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <div className="w-48 bg-white border-r border-gray-200 p-4">
          <nav className="space-y-1">
            {SIDEBAR_TABS.map(tab => (
              <div
                key={tab.key}
                className={`w-full text-left px-3 py-2 text-sm rounded ${
                  tab.key === 'cockpit' ? 'bg-amber-50 text-amber-700 border-l-2 border-amber-500 font-medium' : 'text-gray-600'
                }`}
              >
                {t(SIDEBAR_LABEL_KEYS[tab.key])}
              </div>
            ))}
          </nav>
        </div>
        <div className="flex-1 p-6">
          <SkeletonLoader type="dashboard" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Left Sidebar - Simple text tabs */}
      <div className="w-48 bg-white border-r border-gray-200 p-4">
        <nav className="space-y-1">
          {SIDEBAR_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`w-full text-left px-3 py-2 text-sm rounded transition-colors ${
                activeTab === tab.key
                  ? 'bg-amber-50 text-amber-700 border-l-2 border-amber-500 font-medium'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {t(SIDEBAR_LABEL_KEYS[tab.key])}
            </button>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6">
        {activeTab === 'cockpit' && (
          <div className="space-y-6">
            {/* Top Row: Two big KPI cards */}
            <div className="grid grid-cols-2 gap-6">
              {/* Disponibilité Flotte */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-gray-700 font-medium mb-2">{t('cockpit.main.availabilityTitle')}</h3>
                <div className={`text-5xl font-bold ${stats.availabilityRate >= 90 ? 'text-green-600' : stats.availabilityRate >= 70 ? 'text-amber-500' : 'text-red-600'}`}>
                  {stats.availabilityRate.toFixed(0)} %
                </div>
                <p className="text-gray-500 text-sm mt-1">
                  {t('cockpit.main.availabilitySubtitle', { active: stats.activeVehicles, total: stats.totalVehicles })}
                </p>
                <button
                  onClick={() => setActiveTab('flotte')}
                  className="mt-4 px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
                >
                  {t('cockpit.main.viewStatuses')}
                </button>
              </div>

              {/* Emmerdements en cours */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-gray-700 font-medium mb-2">{t('cockpit.main.issuesTitle')}</h3>
                <div className="text-5xl font-bold text-amber-500">
                  {stats.openClaims} <span className="text-2xl font-normal text-gray-600">{t('cockpit.main.openClaims', { count: stats.openClaims })}</span>
                </div>
                <p className="text-gray-500 text-sm mt-1">
                  {stats.activeMaintenance} {t('cockpit.main.activeMaintenance', { count: stats.activeMaintenance })}
                </p>
                <button
                  onClick={() => setActiveTab('alertes')}
                  className="mt-4 px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
                >
                  {t('cockpit.main.viewList')}
                </button>
              </div>
            </div>

            {/* Second Row: Alerts and Top Vehicles */}
            <div className="grid grid-cols-2 gap-6">
              {/* Alertes / Retards */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-gray-700 font-medium mb-4">{t('cockpit.main.alertsTitle')}</h3>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-center gap-2">
                    <AlertTriangle className={`w-4 h-4 ${alerts.expiredLicenses.length > 0 ? 'text-red-500' : 'text-gray-300'}`} />
                    <span className={alerts.expiredLicenses.length > 0 ? 'text-red-700' : 'text-gray-500'}>
                      {t('cockpit.main.licenseExpiredCount', { count: alerts.expiredLicenses.length })}
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <AlertTriangle className={`w-4 h-4 ${alerts.ctOverdue.length > 0 ? 'text-red-500' : 'text-gray-300'}`} />
                    <span className={alerts.ctOverdue.length > 0 ? 'text-red-700' : 'text-gray-500'}>
                      {t('cockpit.main.ctOverdueCount', { count: alerts.ctOverdue.length })}
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <AlertTriangle className={`w-4 h-4 ${alerts.lldEnding.length > 0 ? 'text-amber-500' : 'text-gray-300'}`} />
                    <span className={alerts.lldEnding.length > 0 ? 'text-amber-700' : 'text-gray-500'}>
                      {t('cockpit.main.leaseEndingCount', { count: alerts.lldEnding.length })}
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <AlertTriangle className={`w-4 h-4 ${alerts.maintenanceDue.length > 0 ? 'text-amber-500' : 'text-gray-300'}`} />
                    <span className={alerts.maintenanceDue.length > 0 ? 'text-amber-700' : 'text-gray-500'}>
                      {t('cockpit.main.maintenanceDueCount', { count: alerts.maintenanceDue.length })}
                    </span>
                  </li>
                </ul>
              </div>

              {/* Top 5 véhicules problématiques */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-gray-700 font-medium mb-4">{t('cockpit.main.topProblematicTitle')}</h3>
                <SimpleTable
                  columns={[
                    { key: 'license_plate', label: t('cockpit.main.colPlateShort') },
                    { key: 'model', label: t('common.catalog.model'), render: (_, row) => `${row.make || ''} ${row.model || ''}`.trim() },
                    { key: 'cost', label: t('cockpit.main.colCost', { symbol: scopeSymbol }), render: (v) => moneyScope(v || 0, { decimals: 0 }) },
                  ]}
                  data={problematicVehicles}
                  pageSize={5}
                  emptyMessage={t('cockpit.main.emptyProblematic')}
                  compact
                />
                <button
                  onClick={() => onNavigate && onNavigate('gdpr')}
                  className="mt-4 px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
                >
                  {t('cockpit.main.openGdprModule')}
                </button>
              </div>
            </div>

            {/* GDPR Summary — branché sur /gdpr-compliance/overview-stats, voir GdprComplianceSummary */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-gray-700 font-medium mb-4">{t('cockpit.main.gdprTitle')}</h3>
              <GdprComplianceSummary />
            </div>
          </div>
        )}

        {activeTab === 'flotte' && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-medium mb-4">{t('cockpit.sidebar.fleet')}</h3>
            <SimpleTable
              columns={[
                { key: 'license_plate', label: t('costs.export.plate') },
                { key: 'make', label: t('common.catalog.brand') },
                { key: 'model', label: t('common.catalog.model') },
                { key: 'status', label: t('vehicles.list.columns.status'), render: (v) => (
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    v === 'active' ? 'bg-green-100 text-green-700' :
                    v === 'maintenance' ? 'bg-orange-100 text-orange-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>{v}</span>
                )},
                { key: 'mileage', label: t('cockpit.main.colMileageShort'), render: (v) => v ? fmt.number(v) : '-' },
              ]}
              data={vehicles}
              pageSize={10}
              compact
            />
          </div>
        )}

        {activeTab === 'alertes' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium mb-4">{t('cockpit.main.alertsTitle')}</h3>

              {/* Expired Licenses */}
              {alerts.expiredLicenses.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-red-700 font-medium mb-2">{t('cockpit.main.expiredLicensesTitle', { count: alerts.expiredLicenses.length })}</h4>
                  <SimpleTable
                    columns={[
                      { key: 'name', label: t('dashboard.deadlines.driver'), render: (_, row) => `${row.first_name} ${row.last_name}` },
                      { key: 'license_expiry', label: t('drivers.importModal.columns.expiry'), render: (v) => fmt.date(v) },
                      { key: 'email', label: t('drivers.detail.info.email') },
                    ]}
                    data={alerts.expiredLicenses}
                    pageSize={5}
                    compact
                  />
                </div>
              )}

              {/* CT Overdue */}
              {alerts.ctOverdue.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-red-700 font-medium mb-2">{t('cockpit.main.ctOverdueTitle', { count: alerts.ctOverdue.length })}</h4>
                  <SimpleTable
                    columns={[
                      { key: 'license_plate', label: t('cockpit.main.colPlateShort') },
                      { key: 'model', label: t('vehicles.list.columns.vehicle'), render: (_, row) => `${row.make} ${row.model}` },
                      { key: 'next_service_due', label: t('fuel.detail.date'), render: (v) => v ? fmt.date(v) : '-' },
                    ]}
                    data={alerts.ctOverdue}
                    pageSize={5}
                    compact
                  />
                </div>
              )}

              {/* All clear */}
              {alerts.expiredLicenses.length === 0 && alerts.ctOverdue.length === 0 && alerts.lldEnding.length === 0 && (
                <p className="text-green-600 text-center py-8">{t('cockpit.main.noAlerts')}</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'couts' && (
          <CostAnalysisTab />
        )}

        {activeTab === 'utilisation' && (
          <UtilizationTab vehicles={vehicles} drivers={drivers} />
        )}
      </div>
    </div>
  );
}

export default FleetCockpitView;
